import React from 'react';
import {fromJS, Map} from 'immutable';

import {PriceAlertsStore, LoginStore} from '../../stores';
import PriceAlertsList from './PriceAlertsList';
import PriceAlertsListActions from './PriceAlertsListActions';
import {PriceAlertsAction, ModalsAction} from '../../actions';
import {formatPriceWithUnits} from './helpers';

PriceAlertsStore.initialize();

// ============================================================================
// CONSTANTS
// ============================================================================

const LOADING_MESSAGES = {
  loading: 'Fetching price alerts from server. Please wait.',
  loaded: '',
  error: 'Error loading price alerts.'
};

const STATUS_BADGE_CLASSES = {
  new: 'label label-danger',
  skipped: 'label label-info',
  resolved: 'label label-success',
  invalid: 'label label-default'
};

// ============================================================================
// COLUMN COMPONENTS
// ============================================================================

/**
 * Checkbox row component - Select individual alert
 */
const CheckboxColumn = React.createClass({
  render() {
    const alert = this.props.rowData;
    const {onToggleSelect} = this.props.metadata;
    const isSelected = alert._isSelected || false;
    const isSelectable = alert.status === 'new';

    if (!isSelectable) {
      return <span></span>;
    }

    return (
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggleSelect(alert.id)}
        title={isSelected ? 'Deselect' : 'Select'}
        style={{cursor: 'pointer'}}
      />
    );
  }
});

/**
 * Generic component for displaying nested object properties
 * @param {string} objectKey - Key in rowData to access
 * @param {string} displayKey - Key in the nested object to display
 * @param {string} fallback - Fallback text if object not found
 */
const NestedObjectColumn = (objectKey, displayKey, fallback = 'N/A') => React.createClass({
  render() {
    const obj = this.props.rowData[objectKey];
    return <span style={{wordBreak: 'break-word', whiteSpace: 'normal'}}>
      {obj ? obj[displayKey] : fallback}
    </span>;
  }
});

const SupplierColumn = NestedObjectColumn('Supplier', 'name');

const PlattColumn = React.createClass({
  render() {
    const platt = this.props.rowData.Platt;
    if (!platt) return <span>N/A</span>;
    return <span style={{wordBreak: 'break-word', whiteSpace: 'normal'}}>
      {[platt.code, platt.name].join(' - ')}
    </span>;
  }
});

const PreviousPriceColumn = React.createClass({
  render() {
    const formatted = formatPriceWithUnits(
      this.props.rowData.previousValue,
      this.props.rowData
    );
    return <span>{formatted}</span>;
  }
});

const NewPriceColumn = React.createClass({
  render() {
    const formatted = formatPriceWithUnits(
      this.props.rowData.value,
      this.props.rowData
    );
    return <span>{formatted}</span>;
  }
});

const PercentageChangeColumn = React.createClass({
  render() {
    const change = this.props.data;
    const color = change > 0 ? 'text-danger' : 'text-success';
    return <span className={color}><strong>{change > 0 ? '+' : ''}{parseFloat(change).toFixed(2)}%</strong></span>;
  }
});

const StatusColumn = React.createClass({
  render() {
    const status = this.props.data;
    const badgeClass = STATUS_BADGE_CLASSES[status] || 'label label-default';
    return <span className={badgeClass}>{status.toUpperCase()}</span>;
  }
});

const CreatedAtColumn = React.createClass({
  render() {
    const date = this.props.data;
    if (!date) return <span>—</span>;
    return <span>{require('moment')(date).format('YYYY-MM-DD')}</span>;
  }
});

export default React.createClass({
  getInitialState() {
    return {
      priceAlerts:      [],
      supplier:         null,
      contractInfo:     null,
      selectedAlertIds: [],  // Track selected alert IDs for bulk operations
      loadingStatus: Map({
        status:  'loading',
        message: 'Fetching data from server. Please wait.'
      }),
      searchParams:  Map(),
      user:          LoginStore.get('user')
    };
  },

  componentWillMount() {
    PriceAlertsStore.addChangeListener(this.handleStoreChange);
    LoginStore.addChangeListener(this.handleLoginStoreChange);
    
    // Check if there are filters from route params
    const filters = this.props.location.query || {};
    PriceAlertsAction.fetchAll(filters);
  },

  componentWillReceiveProps(nextProps) {
    // Re-fetch when query params change
    if (nextProps.location.query !== this.props.location.query) {
      const filters = nextProps.location.query || {};
      PriceAlertsAction.fetchAll(filters);
    }
  },

  componentWillUnmount() {
    PriceAlertsStore.removeChangeListener(this.handleStoreChange);
    LoginStore.removeChangeListener(this.handleLoginStoreChange);
  },

  render() {
    const {selectedAlertIds} = this.state;
    
    return (
      <PriceAlertsList
        priceAlerts={this.state.priceAlerts}
        supplier={this.state.supplier}
        contractInfo={this.state.contractInfo}
        loadingStatus={this.state.loadingStatus}
        searchParams={this.state.searchParams}
        user={this.state.user}
        selectedAlertIds={selectedAlertIds}
        onBulkSkip={this.handleBulkSkip}
        onClearSelection={this.handleClearSelection}
        onToggleSelectAll={this.handleToggleSelectAll}
        columnMetadata={this.getColumnMetadata()}
        columnNames={this.getColumnNames()}
      />
    );
  },

  handleStoreChange() {
    const alerts = PriceAlertsStore.get('priceAlerts');
    const entities = PriceAlertsStore.get('entities');
    
    // Convert Map to array and denormalize
    const {denormalizeAlert} = require('./helpers');
    const alertsArray = alerts ? alerts.toArray().map(alert => {
      const denormalized = denormalizeAlert(alert.toJS(), entities);
      
      // Add searchable plattAsText field for filtering (match the display format)
      if (denormalized.Platt && (denormalized.Platt.code || denormalized.Platt.name)) {
        const parts = [denormalized.Platt.code, denormalized.Platt.name].filter(Boolean);
        denormalized.plattAsText = parts.join(' - ');
      } else {
        denormalized.plattAsText = '';
      }
      
      return denormalized;
    }) : [];

    // Get supplier and contract info from the first alert (all alerts should have same supplier/contract when filtered)
    const supplier = alertsArray.length > 0 && alertsArray[0].Supplier 
      ? alertsArray[0].Supplier 
      : null;
    
    // Extract contract info
    const firstAlert = alertsArray.length > 0 ? alertsArray[0] : null;
    
    const contractInfo = firstAlert ? {
      isPublic: firstAlert.isPublic,
      createdAt: firstAlert.createdAt,
      contractId: firstAlert.contractId
    } : null;

    // Clear selections of alerts that are no longer "new" status
    const {selectedAlertIds} = this.state;
    const newSelectedIds = selectedAlertIds.filter(id => {
      const alert = alertsArray.find(a => a.id === id);
      return alert && alert.status === 'new';
    });

    this.setState({
      priceAlerts:      alertsArray,
      supplier:         supplier,
      contractInfo:     contractInfo,
      selectedAlertIds: newSelectedIds,  // Clear invalid selections
      loadingStatus: Map({
        status:  PriceAlertsStore.get('loadingStatus'),
        message: LOADING_MESSAGES[PriceAlertsStore.get('loadingStatus')] || ''
      }),
      searchParams: PriceAlertsStore.get('search')
    });
  },

  handleLoginStoreChange() {
    this.setState({
      user: LoginStore.get('user')
    });
  },

  /**
   * Toggle selection of a single alert
   */
  handleToggleSelect(alertId) {
    const {selectedAlertIds} = this.state;
    
    if (selectedAlertIds.includes(alertId)) {
      this.setState({selectedAlertIds: selectedAlertIds.filter(id => id !== alertId)});
    } else {
      this.setState({selectedAlertIds: [...selectedAlertIds, alertId]});
    }
  },

  /**
   * Toggle select all / deselect all
   */
  handleToggleSelectAll() {
    const {priceAlerts, selectedAlertIds} = this.state;
    const selectableAlerts = priceAlerts.filter(a => a.status === 'new');
    const allSelected = selectableAlerts.length > 0 && 
                       selectableAlerts.every(a => selectedAlertIds.includes(a.id));

    if (allSelected) {
      // Deselect all
      this.setState({selectedAlertIds: []});
    } else {
      // Select all new alerts
      const allIds = selectableAlerts.map(a => a.id);
      this.setState({selectedAlertIds: allIds});
    }
  },

  /**
   * Clear all selections
   */
  handleClearSelection() {
    this.setState({selectedAlertIds: []});
  },

  /**
   * Handle bulk skip of selected alerts
   */
  handleBulkSkip() {
    const {selectedAlertIds, priceAlerts} = this.state;
    const selectedAlerts = priceAlerts.filter(a => selectedAlertIds.includes(a.id));
    
    ModalsAction.show(103, {alerts: selectedAlerts, isBulk: true});
  },

  /**
   * Determine if airport column should be shown
   * Only show for public alerts or when no data is loaded yet
   */
  shouldShowAirportColumn() {
    return this.state.priceAlerts.length === 0 || 
           this.state.priceAlerts.some(alert => alert.isPublic);
  },

  /**
   * Check if alerts are for primary contracts (not public)
   */
  hasPrimaryContractAlerts() {
    return this.state.priceAlerts.some(alert => !alert.isPublic);
  },

  getColumnMetadata() {
    const showAirport = this.shouldShowAirportColumn();
    const hasPrimaryAlerts = this.hasPrimaryContractAlerts();
    
    const columns = [];
    
    // Add checkbox column for selection
    columns.push({
      columnName:  'select',
      displayName: '',
      cssClassName: 'col-md-1 text-center',
      customComponent: CheckboxColumn,
      onToggleSelect: this.handleToggleSelect,
      sortable: false
    });
    
    if (showAirport) {
      columns.push({
        columnName:  'airportIcao',
        displayName: 'Airport',
        cssClassName: 'col-md-1',
        searchInput: 'text'  // Enable filter for secondary/public contracts
      });
    }
    
    columns.push(
      {
        columnName:  'plattAsText',
        displayName: 'Platt',
        cssClassName: 'col-md-1',
        customComponent: PlattColumn,
        searchInput: hasPrimaryAlerts ? 'text' : false  // Enable filter for primary contracts
      },
      {
        columnName:  'previousValue',
        displayName: 'Previous Price',
        cssClassName: 'col-md-1',
        customComponent: PreviousPriceColumn
      },
      {
        columnName:  'value',
        displayName: 'New Price',
        cssClassName: 'col-md-1',
        customComponent: NewPriceColumn
      },
      {
        columnName:  'percentageChange',
        displayName: '% Change',
        cssClassName: 'col-md-1',
        customComponent: PercentageChangeColumn
      },
      {
        columnName:  'status',
        displayName: 'Status',
        cssClassName: 'col-md-1',
        customComponent: StatusColumn
      },
      {
        columnName:  'createdAt',
        displayName: 'Created',
        cssClassName: 'col-md-1',
        customComponent: CreatedAtColumn
      },
      {
        columnName:  'id',
        displayName: 'Actions',
        cssClassName: 'col-md-2',
        customComponent: PriceAlertsListActions,
        user: this.state.user,
        sortable: false
      }
    );
    
    return columns;
  },

  getColumnNames() {
    const showAirport = this.shouldShowAirportColumn();
    
    const columns = ['select'];  // Start with checkbox column
    
    if (showAirport) {
      columns.push('airportIcao');
    }
    
    columns.push('plattAsText', 'previousValue', 'value', 'percentageChange', 'status', 'createdAt', 'id');
    
    return columns;
  }
});
